import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Copy, Loader2, AlertTriangle, Check, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { ExportMenu } from "../components/report/ExportMenu";
import { ExecutiveTab, ArchitectureTab, DesignTab, CodeTab } from "../components/report/TabsA";
import { PagesTab, BlueprintTab, EvidenceTab, RecommendationsTab } from "../components/report/TabsB";
import { http, errMsg, IMAGES, ensureSession } from "../lib/api";

const TABS = [
  ["executive_summary", "Executive Summary"], ["architecture", "Architecture"], ["design", "Design Intelligence"], ["code", "Code & Functionality"],
  ["pages_components", "Pages & Components"], ["blueprint", "Reconstruction Blueprint"], ["evidence", "Evidence"], ["recommendations", "Recommendations"],
];

const Progress = ({ report }) => (
  <div data-testid="investigation-progress" className="mx-auto grid max-w-5xl gap-8 md:grid-cols-[280px_1fr]">
    <div className="relative overflow-hidden border border-[#1f1f1f]">
      <img src={IMAGES.scan} alt="R.E.A. at work" className="anim-kenburns h-[380px] w-full object-cover" />
      <div className="anim-pulse absolute inset-0 bg-[radial-gradient(circle_at_50%_55%,rgba(220,38,38,0.35),transparent_55%)] mix-blend-screen" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
      <p className="font-gothic absolute bottom-4 left-4 text-4xl text-red-600">R.E.A. is inspecting</p>
    </div>
    <div>
      <p className="rea-label text-red-500">// live investigation</p>
      <h1 className="mt-2 break-all font-display text-3xl font-bold sm:text-4xl">{report.url}</h1>
      <p className="mt-2 text-sm text-neutral-400">Real browser capture + AI analysis typically takes 2–4 minutes. You can leave and return using this private link.</p>
      <ol className="mt-6 space-y-2">
        {report.progress.map((p, i) => {
          const last = i === report.progress.length - 1;
          return (
            <li key={i} data-testid={`progress-stage-${i}`} className="flex items-center gap-3 font-mono text-sm">
              {last ? <Loader2 className="h-4 w-4 animate-spin text-red-500" /> : <Check className="h-4 w-4 text-green-500" />}
              <span className={last ? "text-white" : "text-neutral-500"}>{p.stage}</span>
              <span className="ml-auto text-[10px] text-neutral-600">{new Date(p.at).toLocaleTimeString()}</span>
            </li>
          );
        })}
      </ol>
    </div>
  </div>
);

const Failed = ({ report }) => (
  <div data-testid="investigation-failed" className="rea-panel mx-auto max-w-2xl p-8 text-center">
    <AlertTriangle className="mx-auto h-10 w-10 text-red-500" />
    <h1 className="mt-4 font-display text-2xl font-bold">Investigation could not complete</h1>
    <p className="mt-2 break-all font-mono text-xs text-neutral-400">{report.error}</p>
    {report.refunded && <p data-testid="credit-refunded" className="mt-4 text-sm text-[#d4af37]">Your investigation credit has been refunded.</p>}
    <Link to="/" data-testid="back-home-btn" className="mt-6 inline-block bg-red-700 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest hover:bg-red-600">Back to R.E.A.</Link>
  </div>
);

const Workspace = ({ report }) => {
  const [tab, setTab] = useState(TABS[0][0]);
  const r = report.report || {};
  const props = { r, report, ev: report.evidence };
  const View = { executive_summary: ExecutiveTab, architecture: ArchitectureTab, design: DesignTab, code: CodeTab, pages_components: PagesTab,
    blueprint: BlueprintTab, evidence: EvidenceTab, recommendations: RecommendationsTab }[tab];
  const copyLink = () => { navigator.clipboard.writeText(window.location.href); toast.success("Private report link copied"); };
  return (
    <div data-testid="report-workspace">
      <div className="relative -mx-5 mb-6 h-40 overflow-hidden sm:-mx-8 sm:h-52">
        <img src={IMAGES.dossier} alt="" className="anim-kenburns absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/20" />
        <div className="rea-scanlines pointer-events-none absolute inset-0 opacity-60" />
        <p className="font-gothic absolute bottom-3 right-6 text-5xl text-red-600/90 drop-shadow-[0_0_18px_rgba(220,38,38,0.6)] sm:text-7xl">Dossier</p>
      </div>
      <div className="flex flex-col gap-4 border-b border-[#1f1f1f] pb-6 md:flex-row md:items-end md:justify-between">
        <div className="flex items-center gap-4">
          <img src={IMAGES.rea} alt="R.E.A." className="hidden h-16 w-16 border border-red-900/60 object-cover object-top sm:block" />
          <div className="min-w-0">
            <p className="rea-label text-red-500">R.E.A. intelligence dossier</p>
            <h1 data-testid="report-target-url" className="mt-1 break-all font-display text-2xl font-bold sm:text-3xl">{report.url}</h1>
            <p className="mt-1 font-mono text-[11px] text-neutral-500">Completed {new Date(report.completed_at).toLocaleString()}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="copy-report-link-btn" onClick={copyLink} className="flex items-center gap-2 border border-[#333] px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-neutral-300 hover:border-red-600"><Link2 className="h-4 w-4" /> <span className="hidden sm:inline">Private link</span><Copy className="h-3 w-3 sm:hidden" /></button>
          <ExportMenu token={report.token} tab={tab} tabLabel={TABS.find((t) => t[0] === tab)[1]} />
        </div>
      </div>
      <div className="sticky top-16 z-30 overflow-x-auto border-b border-[#1f1f1f] bg-[#050505]/95 backdrop-blur-xl rea-scroll">
        <div role="tablist" className="flex min-w-max gap-0 xl:min-w-0 xl:justify-between">
          {TABS.map(([k, l], i) => (
            <button key={k} role="tab" aria-selected={tab === k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`relative whitespace-nowrap px-2.5 py-4 font-mono text-[11px] uppercase tracking-wider transition-colors ${tab === k ? "text-white" : "text-neutral-500 hover:text-neutral-200"}`}>
              <span className="mr-1.5 text-red-700">{String(i + 1).padStart(2, "0")}</span>{l}
              {tab === k && <span className="absolute inset-x-2 bottom-0 h-0.5 bg-red-600" />}
            </button>
          ))}
        </div>
      </div>
      <div key={tab} className="anim-rise pt-6" data-testid={`tab-panel-${tab}`}><View {...props} /></div>
    </div>
  );
};

export default function Report() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(null);
  const nav = useNavigate();
  useEffect(() => { ensureSession().then((s) => setCredits(s.credits)).catch(() => {}); }, [report?.status]);
  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const { data } = await http.get(`/reports/${token}`);
        setReport(data);
        if (["queued", "running"].includes(data.status)) timer = setTimeout(poll, 2500);
      } catch (e) { setError(errMsg(e)); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [token]);
  return (
    <div className="relative">
      <div className="red-vignette pointer-events-none fixed inset-0" />
      <Navbar credits={credits} onReports={() => nav("/")} />
      <main className="relative z-10 mx-auto max-w-[1400px] px-5 pb-24 pt-10 sm:px-8">
        {error && <p data-testid="report-error" className="text-center text-red-400">{error}</p>}
        {!report && !error && <p className="flex items-center justify-center gap-2 text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading dossier…</p>}
        {report && ["queued", "running"].includes(report.status) && <Progress report={report} />}
        {report?.status === "failed" && <Failed report={report} />}
        {report?.status === "complete" && <Workspace report={report} />}
      </main>
    </div>
  );
}
